import React, { useRef, useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AppSidebar from './AppSidebar';
import AppTopBar from './AppTopBar';
import { useLauncher } from './useLauncher';
import { TOOLS } from './toolRegistry';

// Reusable enterprise shell (sidebar + top bar) so standalone pages match Home.
const AppShell = ({ activeKey, children }) => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const { allowed, favourites, recent, launch, groupOrder, toolOrder, saveSidebarOrder, layout, saveLayout } = useLauncher();
  const searchRef = useRef(null);
  const [mobileNav, setMobileNav] = useState(false);
  const allowedTools = useMemo(() => TOOLS.filter((t) => allowed.has(t.key)), [allowed]);

  useEffect(() => {
    const onKey = (e) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); searchRef.current?.focus(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleLogout = async () => { await logout(); navigate('/login'); };

  return (
    <div className="flex h-screen overflow-hidden bg-[#FAFAFB] text-slate-900 font-sf">
      <div className="hidden lg:block">
        <AppSidebar allowed={allowed} favourites={favourites} recent={recent} user={user} activeKey={activeKey} onLaunch={launch} onNav={navigate} onLogout={handleLogout} groupOrder={groupOrder} toolOrder={toolOrder} onReorder={saveSidebarOrder} layout={layout} onLayoutChange={saveLayout} />
      </div>
      {mobileNav && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-nav-overlay">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setMobileNav(false)} />
          <div className="absolute left-0 top-0 bottom-0 shadow-2xl">
            <AppSidebar allowed={allowed} favourites={favourites} recent={recent} user={user} activeKey={activeKey} onLaunch={(t) => { setMobileNav(false); launch(t); }} onNav={(p) => { setMobileNav(false); navigate(p); }} onLogout={handleLogout} groupOrder={groupOrder} toolOrder={toolOrder} onReorder={saveSidebarOrder} layout={layout} onLayoutChange={saveLayout} />
          </div>
        </div>
      )}
      <div className="flex-1 flex flex-col min-w-0">
        <AppTopBar user={user} tools={allowedTools} recentKeys={recent} favouriteKeys={favourites} searchRef={searchRef} onLaunch={launch} onNav={navigate} onLogout={handleLogout} onMenu={() => setMobileNav(true)} isAdmin={isAdmin} />
        <main className="flex-1 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
};

export default AppShell;
